import { observer } from 'mobx-react-lite';
import { forwardRef, useImperativeHandle, useMemo, useRef, useState, type PointerEventHandler } from 'react';
import { TransformComponent, TransformWrapper, type ReactZoomPanPinchContentRef } from 'react-zoom-pan-pinch';

import type { ScreencastTooltips } from '../../model/screencast-tooltips/types';
import { HighlightOverlay } from '../screencast-tooltip/HighlightOverlay';
import { LineMeasurementOverlay } from '../screencast-tooltip/LineMeasurementOverlay';
import { ScreencastLineMeasurementTooltip } from '../screencast-tooltip/ScreencastLineMeasurementTooltip';
import { ScreencastTooltip } from '../screencast-tooltip/ScreencastTooltip';

import {
  useElementSize,
  useImageNaturalSize,
  useImagePointResolver,
  useLineMeasurementInteraction,
  useScreencastHoverTooltip,
  useShiftPressed,
  type TransformState,
} from './hooks';

const scaleUp = true;
const zoomFactor = 8;

type BroadcastImageProps = {
  src: string;
  alt: string;
  isTooltipVisible: boolean;
  screencastTooltips?: ScreencastTooltips | null;
};

export const BroadcastImage = observer(
  // eslint-disable-next-line mobx/no-anonymous-observer
  forwardRef(function BroadcastImage({ src, alt, screencastTooltips, isTooltipVisible }: BroadcastImageProps, ref) {
    const reactZoomPanPinchContent = useRef<ReactZoomPanPinchContentRef | null>(null);
    const {
      height: containerHeight,
      setElement: setContainerElement,
      width: containerWidth,
    } = useElementSize<HTMLDivElement>();
    const imageNaturalSize = useImageNaturalSize(src);
    const isShiftPressed = useShiftPressed();
    const [transformState, setTransformState] = useState<TransformState>({
      scale: 1,
      positionX: 0,
      positionY: 0,
    });

    const imageScale = useMemo(() => {
      if (
        containerWidth === 0 ||
        containerHeight === 0 ||
        imageNaturalSize.width === 0 ||
        imageNaturalSize.height === 0
      ) {
        return 0;
      }

      const scale = Math.min(containerWidth / imageNaturalSize.width, containerHeight / imageNaturalSize.height);
      return scaleUp ? scale : Math.max(scale, 1);
    }, [containerHeight, containerWidth, imageNaturalSize.height, imageNaturalSize.width]);

    const getImagePointFromClientPosition = useImagePointResolver({
      imageNaturalWidth: imageNaturalSize.width,
      imageNaturalHeight: imageNaturalSize.height,
      transformState,
    });

    const hoverTooltip = useScreencastHoverTooltip({
      isEnabled: isTooltipVisible,
      screencastTooltips,
      getImagePointFromClientPosition,
    });

    const lineMeasurement = useLineMeasurementInteraction({
      isEnabled: isTooltipVisible,
      screencastTooltips,
      getImagePointFromClientPosition,
      onStart: hoverTooltip.clearTooltip,
    });
    const activeLineMeasurement = lineMeasurement.activeLineMeasurement;

    useImperativeHandle(ref, () => {
      return {
        zoomIn: reactZoomPanPinchContent.current?.zoomIn,
        zoomOut: reactZoomPanPinchContent.current?.zoomOut,
        setTransform: reactZoomPanPinchContent.current?.setTransform,
        resetTransform: reactZoomPanPinchContent.current?.resetTransform,
        centerView: reactZoomPanPinchContent.current?.centerView,
        zoomToElement: reactZoomPanPinchContent.current?.zoomToElement,
      };
    });

    const handlePointerMove: PointerEventHandler<HTMLDivElement> = (event) => {
      if (activeLineMeasurement) {
        lineMeasurement.handlePointerMove(event);
        return;
      }

      hoverTooltip.handlePointerMove(event);
    };

    const handleMouseLeave = () => {
      if (activeLineMeasurement) {
        return;
      }

      hoverTooltip.handleMouseLeave();
    };

    const visibleResolvedTooltip = isTooltipVisible && !activeLineMeasurement ? hoverTooltip.resolvedTooltip : null;
    const visibleTooltipScreenPosition =
      isTooltipVisible && !activeLineMeasurement ? hoverTooltip.tooltipScreenPosition : null;
    const isLineMeasurementStartCursorVisible = isTooltipVisible && Boolean(screencastTooltips) && isShiftPressed;

    return (
      <div style={{ height: '100%', position: 'relative' }} ref={setContainerElement}>
        {imageScale > 0 && (
          <TransformWrapper
            key={`${containerWidth}x${containerHeight}-${imageNaturalSize.width}x${imageNaturalSize.height}`}
            initialScale={imageScale}
            minScale={imageScale}
            maxScale={imageScale * zoomFactor}
            centerOnInit
            ref={reactZoomPanPinchContent}
            panning={{ disabled: Boolean(activeLineMeasurement) }}
            onInit={(instance) => {
              setTransformState(instance.state);
            }}
            onTransformed={(_, state) => {
              setTransformState(state);
            }}
          >
            <TransformComponent
              wrapperStyle={{
                width: '100%',
                height: '100%',
                cursor: activeLineMeasurement
                  ? 'default'
                  : isLineMeasurementStartCursorVisible
                    ? 'crosshair'
                    : undefined,
              }}
              wrapperProps={{
                onMouseDownCapture: lineMeasurement.handleMouseDownCapture,
                onMouseLeave: handleMouseLeave,
                onPointerDown: lineMeasurement.handlePointerDown,
                onPointerMove: handlePointerMove,
                onPointerUp: lineMeasurement.handlePointerUp,
                onPointerCancel: lineMeasurement.handlePointerUp,
              }}
            >
              <div style={{ position: 'relative', lineHeight: 0 }}>
                <img alt={alt} src={src} style={{ display: 'block' }} />
                {visibleResolvedTooltip && (
                  <HighlightOverlay
                    imageNaturalWidth={imageNaturalSize.width}
                    imageNaturalHeight={imageNaturalSize.height}
                    areas={visibleResolvedTooltip.highlightAreas}
                  />
                )}
                {activeLineMeasurement && (
                  <LineMeasurementOverlay
                    imageNaturalWidth={imageNaturalSize.width}
                    imageNaturalHeight={imageNaturalSize.height}
                    start={activeLineMeasurement.start}
                    end={activeLineMeasurement.current}
                    scale={transformState.scale}
                  />
                )}
              </div>
            </TransformComponent>
          </TransformWrapper>
        )}
        <ScreencastTooltip tooltip={visibleResolvedTooltip} cursorPosition={visibleTooltipScreenPosition} />
        <ScreencastLineMeasurementTooltip
          measurement={activeLineMeasurement?.measurement ?? null}
          cursorPosition={activeLineMeasurement?.cursorPosition ?? null}
        />
      </div>
    );
  }),
);
